import { useState, useCallback } from "react";

// ─── Palette ──────────────────────────────────────────────────────────────────

const C = {
  // Brand
  primary: "#5E78EE",
  primaryDark: "#4B66D1",
  primaryLight: "rgba(94,120,238,0.15)",
  primaryGlow: "rgba(94,120,238,0.25)",
  // Dark background layers
  bg: "#1A2340", // page background
  surface: "#202C50", // card background
  surfaceRaised: "#263358", // inset / sub-section
  border: "rgba(255,255,255,0.10)",
  borderFocus: "#5E78EE",
  // Text
  text: "#F0F3FF",
  textMid: "#9BA8C8",
  textSoft: "#5F6E92",
  // Semantic
  error: "#F26B8A",
  errorLight: "rgba(242,107,138,0.12)",
  errorBorder: "rgba(242,107,138,0.35)",
  success: "#3EDFA8",
  successLight: "rgba(62,223,168,0.12)",
};

const GLOBAL_STYLE = `
  @import url('https://fonts.googleapis.com/css2?family=Sora:wght@400;600;700&family=DM+Sans:wght@300;400;500;600&display=swap');
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  html, body { background: ${C.bg}; }
  input, select, textarea, button {
    font-family: 'DM Sans', sans-serif;
  }
  input:focus, select:focus, textarea:focus {
    outline: none;
    border-color: ${C.borderFocus} !important;
    box-shadow: 0 0 0 3px ${C.primaryGlow} !important;
  }
  input::placeholder, textarea::placeholder { color: ${C.textSoft}; }
  select option { background: #1e2b4a; color: #f0f3ff; }
  @keyframes fadeUp {
    from { opacity: 0; transform: translateY(16px); }
    to   { opacity: 1; transform: translateY(0); }
  }
  .fade-up { animation: fadeUp 0.35s ease both; }
  ::-webkit-scrollbar { width: 6px; }
  ::-webkit-scrollbar-track { background: ${C.bg}; }
  ::-webkit-scrollbar-thumb { background: ${C.surfaceRaised}; border-radius: 3px; }
`;

// ─── Types ────────────────────────────────────────────────────────────────────

export interface QuickAddPatientPayload {
  FirstName: string;
  MiddleName: string;
  LastName: string;
  preferredName: string;
  pronounsId: string;
  pronounSelfDescribe: string;
  Sex: string;
  genderIdentityId: string;
  genderSelfDescribed: string;
  SuffixName: string;
  DOBMonth: string;
  DOBDay: string;
  DOBYear: string;
  custodyStatus: string | null;
  parentGuardian: string;
  Address1: string;
  Address2: string;
  City: string;
  State: string;
  StateText: string;
  PostalCode: string;
  Country: string;
  PrimaryContact: string;
  AreaCode: string;
  Number1: string;
  Number2: string;
  AppointmentRemindersHome: string;
  SecondaryAreaCode: string;
  SecondaryNumber1: string;
  SecondaryNumber2: string;
  AppointmentRemindersMobile: string;
  emailReminder: string;
  AppointmentReminders: string;
  caseTitle: string;
  RelatedCause: string;
  AccidentDate: string;
  AccidentStateProvince: string;
  additionalInformation: string;
  PrimaryInsurance: string;
  PrimaryIsGuarantor: string;
  PrimaryInsuredParty: string;
  PrimaryPolicySubscriberNumber: string;
  PrimaryPolicyGroupNumber: string;
  WComp_ClaimNumber: string;
  WComp_FirstName: string;
  WComp_PhoneAreaCode: string;
  WComp_PhoneLocalNumber1: string;
  WComp_PhoneLocalNumber2: string;
  WComp_Address1: string;
  WComp_Address2: string;
  WComp_City: string;
  WComp_State: string;
  WComp_StateText: string;
  WComp_PostalCode: string;
  WComp_Country: string;
  guarantorFirstName: string;
  guarantorMiddleName: string;
  guarantorLastName: string;
  guarantorGender: string;
  guarantorDOBMonth: string;
  guarantorDOBDay: string;
  guarantorDOBYear: string;
  guarantorSSN: string;
  guarantorPatientRelation: string;
  guarantorPhoneAreaCode: string;
  guarantorPhoneLocalNumber1: string;
  guarantorPhoneLocalNumber2: string;
  Guarantor_Address1: string;
  Guarantor_Address2: string;
  Guarantor_City: string;
  Guarantor_State: string;
  Guarantor_StateText: string;
  Guarantor_PostalCode: string;
  Guarantor_Country: string;
  PrimaryResponsiblePartyFirstName: string;
  PrimaryResponsiblePartyMiddleName: string;
  PrimaryResponsiblePartyLastName: string;
  PrimaryResponsiblePartyDOBMonth: string;
  PrimaryResponsiblePartyDOBDay: string;
  PrimaryResponsiblePartyDOBYear: string;
  PrimaryResponsiblePartyGender: string;
  PrimaryResponsiblePartyPhoneAreaCode: string;
  PrimaryResponsiblePartyPhoneLocalNumber1: string;
  PrimaryResponsiblePartyPhoneLocalNumber2: string;
  PrimaryResponsiblePartySSN: string;
  PrimaryResponsiblePartyInsurerIdentifier: string;
  PrimaryResponsibleParty_Address1: string;
  PrimaryResponsibleParty_Address2: string;
  PrimaryResponsibleParty_City: string;
  PrimaryResponsibleParty_State: string;
  PrimaryResponsibleParty_StateText: string;
  PrimaryResponsibleParty_PostalCode: string;
  PrimaryResponsibleParty_Country: string;
  primaryInsuranceAuthorizationRequiredYesNo: string;
  primaryInsuranceAuthorizationNumber: string;
  effectiveStart: string;
  effectiveEnd: string;
  authorizationAmountType: string;
  countAuthorized: string;
  authPhoneType: string;
  authTelephoneAreaCode: string;
  authTelephoneLocalNumber1: string;
  authTelephoneLocalNumber2: string;
  additionalInfoAuth: string;
  HasSecondaryInsuranceYesNo: string;
  SecondaryInsurance: string;
  SecondaryInsuredParty: string;
  SecondaryPolicySubscriberNumber: string;
  SecondaryPolicyGroupNumber: string;
  SecondaryResponsiblePartyFirstName: string;
  SecondaryResponsiblePartyMiddleName: string;
  SecondaryResponsiblePartyLastName: string;
  SecondaryResponsiblePartyDOBMonth: string;
  SecondaryResponsiblePartyDOBDay: string;
  SecondaryResponsiblePartyDOBYear: string;
  SecondaryResponsiblePartyGender: string;
  SecondaryResponsiblePartyPhoneAreaCode: string;
  SecondaryResponsiblePartyPhoneLocalNumber1: string;
  SecondaryResponsiblePartyPhoneLocalNumber2: string;
  SecondaryResponsiblePartySSN: string;
  SecondaryResponsiblePartyInsurerIdentifier: string;
  SecondaryResponsibleParty_Address1: string;
  SecondaryResponsibleParty_Address2: string;
  SecondaryResponsibleParty_City: string;
  SecondaryResponsibleParty_State: string;
  SecondaryResponsibleParty_StateText: string;
  SecondaryResponsibleParty_PostalCode: string;
  SecondaryResponsibleParty_Country: string;
  IsReferredByPhysicianYesNo: string;
  ReferralDate: string;
  Physician: string;
  MarketingReferral_ContactID: string;
  RequestedProvider: string;
  Facilities: string;
  primaryTreatmentClinic: number | string;
  DefaultPlaceOfServiceId: number | string;
  prescriptionInformationYesNo: string;
  prescriptionStartDate: string;
  prescriptionEndDate: string;
  prescriptionNumberOfVisits: string;
  prescriptionAlertDaysPrior: string;
  prescriptionAlertVisitsPrior: string;
  prescriptionFrequency: string;
  prescriptionCustomFrequency: string;
  prescriptionDuration: string;
  prescriptionCustomDuration: string;
  icd9Combo: string;
  UserDefName: string;
  documentType: string;
  receiptDate: string;
  expirationDate: string;
  dateSigned: string;
  SignerPhysician: string;
  TherapistID: number | string;
  addDpiSelectorOnNewCaseYesNo: string;
  extDoc: boolean | string;
  SignerPhysicianID: string;
  documentTypeID: string;
  PrimaryInsID: number | string;
  SecondaryInsID: number | string;
  testIcd10: string;
  PhysicianID: number | string;
  icdCodes: unknown[];
  requestedProvider: string;
}

type FormErrors = Partial<Record<string, string>>;

// ─── Static Mock Data ─────────────────────────────────────────────────────────

const MOCK_INSURANCES = [
  { InsID: 139480, InsName: "Medicare – Noridian JE Part B", InsTypeID: 3 },
  { InsID: 1, InsName: "Self Pay", InsTypeID: 1 },
  { InsID: 2, InsName: "Aetna", InsTypeID: 2 },
  { InsID: 3, InsName: "Blue Cross Blue Shield", InsTypeID: 2 },
  { InsID: 4, InsName: "Cigna", InsTypeID: 2 },
  { InsID: 5, InsName: "United Healthcare", InsTypeID: 2 },
  { InsID: 6, InsName: "Humana", InsTypeID: 2 },
  { InsID: 7, InsName: "Workers Compensation", InsTypeID: 7 },
];

const MOCK_PHYSICIANS = [
  { PhysicianID: 8578947, Name: "Cristina A. Perez, NP", Identification: "1730718503" },
  { PhysicianID: 1001, Name: "John Smith, MD", Identification: "1234567890" },
  { PhysicianID: 1002, Name: "Sarah Jones, DO", Identification: "0987654321" },
];

const MOCK_THERAPISTS = [
  { TherapistID: 18147, Name: "Default Therapist" },
  { TherapistID: 18148, Name: "Therapist B" },
];

const MOCK_FACILITIES = [
  { value: 2916, display: "Main Campus" },
  { value: 2914, display: "North Clinic" },
  { value: 2910, display: "South Clinic" },
  { value: 2915, display: "East Wing" },
  { value: 2911, display: "West Outpatient" },
];

const US_STATES: [string, string][] = [
  ["AL", "Alabama"],
  ["AK", "Alaska"],
  ["AZ", "Arizona"],
  ["AR", "Arkansas"],
  ["CA", "California"],
  ["CO", "Colorado"],
  ["CT", "Connecticut"],
  ["DC", "District of Columbia"],
  ["DE", "Delaware"],
  ["FL", "Florida"],
  ["GA", "Georgia"],
  ["HI", "Hawaii"],
  ["ID", "Idaho"],
  ["IL", "Illinois"],
  ["IN", "Indiana"],
  ["IA", "Iowa"],
  ["KS", "Kansas"],
  ["KY", "Kentucky"],
  ["LA", "Louisiana"],
  ["ME", "Maine"],
  ["MD", "Maryland"],
  ["MA", "Massachusetts"],
  ["MI", "Michigan"],
  ["MN", "Minnesota"],
  ["MS", "Mississippi"],
  ["MO", "Missouri"],
  ["MT", "Montana"],
  ["NE", "Nebraska"],
  ["NV", "Nevada"],
  ["NH", "New Hampshire"],
  ["NJ", "New Jersey"],
  ["NM", "New Mexico"],
  ["NY", "New York"],
  ["NC", "North Carolina"],
  ["ND", "North Dakota"],
  ["OH", "Ohio"],
  ["OK", "Oklahoma"],
  ["OR", "Oregon"],
  ["PA", "Pennsylvania"],
  ["RI", "Rhode Island"],
  ["SC", "South Carolina"],
  ["SD", "South Dakota"],
  ["TN", "Tennessee"],
  ["TX", "Texas"],
  ["UT", "Utah"],
  ["VT", "Vermont"],
  ["VA", "Virginia"],
  ["WA", "Washington"],
  ["WV", "West Virginia"],
  ["WI", "Wisconsin"],
  ["WY", "Wyoming"],
  ["AA", "Armed Forces Americas"],
  ["AE", "Armed Forces Europe"],
  ["AP", "Armed Forces Pacific"],
];

const CA_PROVINCES: [string, string][] = [
  ["AB", "Alberta"],
  ["BC", "British Columbia"],
  ["MB", "Manitoba"],
  ["NB", "New Brunswick"],
  ["NL", "Newfoundland and Labrador"],
  ["NS", "Nova Scotia"],
  ["ON", "Ontario"],
  ["PE", "Prince Edward Island"],
  ["QC", "Quebec"],
  ["SK", "Saskatchewan"],
  ["NT", "Northwest Territories"],
  ["NU", "Nunavut"],
  ["YT", "Yukon"],
];

const COUNTRIES: [string, string][] = [
  ["US", "United States"],
  ["CA", "Canada"],
  ["MX", "Mexico"],
  ["AU", "Australia"],
  ["AT", "Austria"],
  ["BE", "Belgium"],
  ["BR", "Brazil"],
  ["CN", "China"],
  ["DK", "Denmark"],
  ["EG", "Egypt"],
  ["FR", "France"],
  ["DE", "Germany"],
  ["GR", "Greece"],
  ["IN", "India"],
  ["IE", "Ireland"],
  ["IL", "Israel"],
  ["IT", "Italy"],
  ["JP", "Japan"],
  ["NL", "Netherlands"],
  ["NZ", "New Zealand"],
  ["NG", "Nigeria"],
  ["NO", "Norway"],
  ["PK", "Pakistan"],
  ["PH", "Philippines"],
  ["PL", "Poland"],
  ["PT", "Portugal"],
  ["RU", "Russian Federation"],
  ["SA", "Saudi Arabia"],
  ["ZA", "South Africa"],
  ["ES", "Spain"],
  ["SE", "Sweden"],
  ["CH", "Switzerland"],
  ["TR", "Turkey"],
  ["UA", "Ukraine"],
  ["AE", "United Arab Emirates"],
  ["GB", "United Kingdom"],
  ["UY", "Uruguay"],
  ["VN", "Viet Nam"],
];

const RELATED_CAUSES: [string, string][] = [
  ["", "None / Not Applicable"],
  ["AA", "Auto Accident"],
  ["AF", "Auto Accident – Other Party Fault"],
  ["EM", "Employment Related"],
  ["OA", "Other Accident"],
  ["OT", "Other"],
];
const INSURED_PARTY_OPTIONS: [string, string][] = [
  ["1", "Self"],
  ["2", "Spouse"],
  ["3", "Child"],
  ["4", "Mother"],
  ["5", "Father"],
  ["6", "Other"],
  ["7", "Unknown"],
];
const PATIENT_RELATION_OPTIONS: [string, string][] = [
  ["1", "Self"],
  ["2", "Spouse"],
  ["3", "Child"],
  ["4", "Mother"],
  ["5", "Father"],
  ["6", "Other"],
  ["7", "Unknown"],
];
const DOCUMENT_TYPES: [string, string][] = [
  ["9", "Blood Work / Labs"],
  ["10", "Driver's License"],
  ["14", "HEP"],
  ["11", "Insurance Card"],
  ["8", "Medication Listing"],
  ["6", "MRI"],
  ["3", "Other"],
  ["13", "Past Medical History"],
  ["15", "Patient Intake"],
  ["7", "Physician's Notes"],
  ["12", "Plan of Care"],
  ["5", "X-Ray"],
];
const AUTH_PHONE_TYPES: [string, string][] = [
  ["PRN", "Home"],
  ["WPN", "Work"],
  ["PRS", "Mobile"],
  ["EMR", "Emergency"],
  ["ASN", "Answering Service"],
  ["NOK", "Next-of-Kin"],
  ["POA", "Power of Attorney"],
];
const PRESCRIPTION_FREQUENCIES: [string, string][] = [
  ["Custom", "Custom"],
  ["1 time visit only", "1× Visit Only"],
  ["1 time a week", "1× / Week"],
  ["1-2 times a week", "1–2× / Week"],
  ["2 times a week", "2× / Week"],
  ["2-3 times a week", "2–3× / Week"],
  ["3 times a week", "3× / Week"],
  ["3-4 times a week", "3–4× / Week"],
  ["4 times a week", "4× / Week"],
  ["BID", "BID"],
  ["QD", "QD"],
  ["PRN", "PRN"],
];
const PRESCRIPTION_DURATIONS: [string, string][] = [
  ["Custom", "Custom"],
  ["N/A", "N/A"],
  ["1 week", "1 Wk"],
  ["2 weeks", "2 Wks"],
  ["3 weeks", "3 Wks"],
  ["4 weeks", "4 Wks"],
  ["5 weeks", "5 Wks"],
  ["6 weeks", "6 Wks"],
  ["8 weeks", "8 Wks"],
  ["10 weeks", "10 Wks"],
  ["12 weeks", "12 Wks"],
];
const TODAY = new Date().toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "numeric" });

// ─── Initial State ─────────────────────────────────────────────────────────────

const initialState: QuickAddPatientPayload = {
  FirstName: "",
  MiddleName: "",
  LastName: "",
  preferredName: "",
  pronounsId: "",
  pronounSelfDescribe: "",
  Sex: "",
  genderIdentityId: "",
  genderSelfDescribed: "",
  SuffixName: "",
  DOBMonth: "",
  DOBDay: "",
  DOBYear: "",
  custodyStatus: null,
  parentGuardian: "",
  Address1: "",
  Address2: "",
  City: "",
  State: "",
  StateText: "",
  PostalCode: "",
  Country: "US",
  PrimaryContact: "Home",
  AreaCode: "",
  Number1: "",
  Number2: "",
  AppointmentRemindersHome: "None",
  SecondaryAreaCode: "",
  SecondaryNumber1: "",
  SecondaryNumber2: "",
  AppointmentRemindersMobile: "None",
  emailReminder: "",
  AppointmentReminders: "None",
  caseTitle: "",
  RelatedCause: "",
  AccidentDate: TODAY,
  AccidentStateProvince: "CA",
  additionalInformation: "",
  PrimaryInsurance: "",
  PrimaryIsGuarantor: "on",
  PrimaryInsuredParty: "Self",
  PrimaryPolicySubscriberNumber: "",
  PrimaryPolicyGroupNumber: "",
  WComp_ClaimNumber: "",
  WComp_FirstName: "",
  WComp_PhoneAreaCode: "",
  WComp_PhoneLocalNumber1: "",
  WComp_PhoneLocalNumber2: "",
  WComp_Address1: "",
  WComp_Address2: "",
  WComp_City: "",
  WComp_State: "",
  WComp_StateText: "",
  WComp_PostalCode: "",
  WComp_Country: "US",
  guarantorFirstName: "",
  guarantorMiddleName: "",
  guarantorLastName: "",
  guarantorGender: "",
  guarantorDOBMonth: "",
  guarantorDOBDay: "",
  guarantorDOBYear: "",
  guarantorSSN: "",
  guarantorPatientRelation: "",
  guarantorPhoneAreaCode: "",
  guarantorPhoneLocalNumber1: "",
  guarantorPhoneLocalNumber2: "",
  Guarantor_Address1: "",
  Guarantor_Address2: "",
  Guarantor_City: "",
  Guarantor_State: "",
  Guarantor_StateText: "",
  Guarantor_PostalCode: "",
  Guarantor_Country: "US",
  PrimaryResponsiblePartyFirstName: "",
  PrimaryResponsiblePartyMiddleName: "",
  PrimaryResponsiblePartyLastName: "",
  PrimaryResponsiblePartyDOBMonth: "",
  PrimaryResponsiblePartyDOBDay: "",
  PrimaryResponsiblePartyDOBYear: "",
  PrimaryResponsiblePartyGender: "",
  PrimaryResponsiblePartyPhoneAreaCode: "",
  PrimaryResponsiblePartyPhoneLocalNumber1: "",
  PrimaryResponsiblePartyPhoneLocalNumber2: "",
  PrimaryResponsiblePartySSN: "",
  PrimaryResponsiblePartyInsurerIdentifier: "",
  PrimaryResponsibleParty_Address1: "",
  PrimaryResponsibleParty_Address2: "",
  PrimaryResponsibleParty_City: "",
  PrimaryResponsibleParty_State: "",
  PrimaryResponsibleParty_StateText: "",
  PrimaryResponsibleParty_PostalCode: "",
  PrimaryResponsibleParty_Country: "US",
  primaryInsuranceAuthorizationRequiredYesNo: "0",
  primaryInsuranceAuthorizationNumber: "",
  effectiveStart: TODAY,
  effectiveEnd: TODAY,
  authorizationAmountType: "VISITS",
  countAuthorized: "",
  authPhoneType: "",
  authTelephoneAreaCode: "",
  authTelephoneLocalNumber1: "",
  authTelephoneLocalNumber2: "",
  additionalInfoAuth: "",
  HasSecondaryInsuranceYesNo: "0",
  SecondaryInsurance: "",
  SecondaryInsuredParty: "Self",
  SecondaryPolicySubscriberNumber: "",
  SecondaryPolicyGroupNumber: "",
  SecondaryResponsiblePartyFirstName: "",
  SecondaryResponsiblePartyMiddleName: "",
  SecondaryResponsiblePartyLastName: "",
  SecondaryResponsiblePartyDOBMonth: "",
  SecondaryResponsiblePartyDOBDay: "",
  SecondaryResponsiblePartyDOBYear: "",
  SecondaryResponsiblePartyGender: "",
  SecondaryResponsiblePartyPhoneAreaCode: "",
  SecondaryResponsiblePartyPhoneLocalNumber1: "",
  SecondaryResponsiblePartyPhoneLocalNumber2: "",
  SecondaryResponsiblePartySSN: "",
  SecondaryResponsiblePartyInsurerIdentifier: "",
  SecondaryResponsibleParty_Address1: "",
  SecondaryResponsibleParty_Address2: "",
  SecondaryResponsibleParty_City: "",
  SecondaryResponsibleParty_State: "",
  SecondaryResponsibleParty_StateText: "",
  SecondaryResponsibleParty_PostalCode: "",
  SecondaryResponsibleParty_Country: "US",
  IsReferredByPhysicianYesNo: "0",
  ReferralDate: TODAY,
  Physician: "",
  MarketingReferral_ContactID: "",
  RequestedProvider: "",
  Facilities: "",
  primaryTreatmentClinic: "",
  DefaultPlaceOfServiceId: 4,
  prescriptionInformationYesNo: "0",
  prescriptionStartDate: "",
  prescriptionEndDate: "",
  prescriptionNumberOfVisits: "",
  prescriptionAlertDaysPrior: "5",
  prescriptionAlertVisitsPrior: "5",
  prescriptionFrequency: "Custom",
  prescriptionCustomFrequency: "",
  prescriptionDuration: "Custom",
  prescriptionCustomDuration: "",
  icd9Combo: "",
  UserDefName: "",
  documentType: "Patient Intake",
  receiptDate: TODAY,
  expirationDate: "",
  dateSigned: "",
  SignerPhysician: "",
  TherapistID: "",
  addDpiSelectorOnNewCaseYesNo: "1",
  extDoc: false,
  SignerPhysicianID: "",
  documentTypeID: "15",
  PrimaryInsID: "",
  SecondaryInsID: "",
  testIcd10: "off",
  PhysicianID: "",
  icdCodes: [],
  requestedProvider: "",
};

// ─── Validation ───────────────────────────────────────────────────────────────

interface ValidationCtx {
  showGuarantorPanel: boolean;
  showWorkerComp: boolean;
  showPrimaryInsuredParty: boolean;
  showPrimaryRPPanel: boolean;
  showSecondaryRPPanel: boolean;
  showAuthDetails: boolean;
  showPrescriptionDetails: boolean;
}

function phoneOk(a: string, n1: string, n2: string) {
  return a.trim().length === 3 && n1.trim().length === 3 && n2.trim().length === 4;
}
function validateAddr(f: Record<string, string>, prefix: string, required: boolean, e: FormErrors) {
  const anyTouched = !!(
    f[`${prefix}Address1`]?.trim() ||
    f[`${prefix}Address2`]?.trim() ||
    f[`${prefix}City`]?.trim() ||
    f[`${prefix}PostalCode`]?.trim() ||
    f[`${prefix}State`]?.trim() ||
    f[`${prefix}StateText`]?.trim()
  );
  if (!required && !anyTouched) return;
  const country = f[`${prefix}Country`] || "US";
  const isUSCA = country === "US" || country === "CA";
  if (!f[`${prefix}Address1`]?.trim()) e[`${prefix}Address1`] = "Required";
  if (!f[`${prefix}City`]?.trim()) e[`${prefix}City`] = "Required";
  if (isUSCA && !f[`${prefix}State`]?.trim()) e[`${prefix}State`] = "Required";
  if (!isUSCA && !f[`${prefix}StateText`]?.trim()) e[`${prefix}StateText`] = "Required";
  if (isUSCA && !f[`${prefix}PostalCode`]?.trim()) e[`${prefix}PostalCode`] = "Required";
}
function validate(form: QuickAddPatientPayload, ctx: ValidationCtx): FormErrors {
  const e: FormErrors = {};
  const f = form as unknown as Record<string, string>;
  if (!form.FirstName.trim()) e.FirstName = "First name is required";
  if (!form.LastName.trim()) e.LastName = "Last name is required";
  if (!form.Sex) e.Sex = "Please select a sex";
  if (!form.DOBMonth || !form.DOBDay || !form.DOBYear) e.DOB = "Full date of birth is required";
  const homeOk = phoneOk(form.AreaCode, form.Number1, form.Number2);
  const mobileOk = phoneOk(form.SecondaryAreaCode, form.SecondaryNumber1, form.SecondaryNumber2);
  if (!homeOk && !mobileOk) e.phone = "At least one complete phone number is required";
  if (form.PrimaryContact === "Home" && !homeOk) e.homePhone = "Home phone required for selected primary contact";
  if (form.PrimaryContact === "Mobile" && !mobileOk) e.mobilePhone = "Mobile phone required for selected primary contact";
  if ((form.AppointmentReminders === "1" || form.PrimaryContact === "Email") && !form.emailReminder.trim()) e.emailReminder = "Email address is required";
  if (!form.PrimaryInsID || Number(form.PrimaryInsID) <= 0) e.PrimaryInsID = "Primary insurance is required";
  if (!form.PhysicianID || Number(form.PhysicianID) <= 0) e.PhysicianID = "Physician is required";
  if (!form.TherapistID || Number(form.TherapistID) <= 0) e.TherapistID = "Therapist is required";
  validateAddr(f, "", true, e);
  if (ctx.showWorkerComp) {
    if (!form.WComp_ClaimNumber.trim()) e.WComp_ClaimNumber = "Claim number required";
    if (!form.WComp_FirstName.trim()) e.WComp_FirstName = "Employer name required";
    if (!phoneOk(form.WComp_PhoneAreaCode, form.WComp_PhoneLocalNumber1, form.WComp_PhoneLocalNumber2)) e.WComp_Phone = "Complete employer phone required";
    validateAddr(f, "WComp_", true, e);
  }
  if (ctx.showGuarantorPanel) {
    if (!form.guarantorFirstName.trim()) e.guarantorFirstName = "Required";
    if (!form.guarantorLastName.trim()) e.guarantorLastName = "Required";
    if (!form.guarantorGender) e.guarantorGender = "Required";
    if (!form.guarantorDOBMonth || !form.guarantorDOBDay || !form.guarantorDOBYear) e.guarantorDOB = "Full DOB required";
    if (!form.guarantorPatientRelation) e.guarantorPatientRelation = "Required";
    validateAddr(f, "Guarantor_", false, e);
  }
  if (ctx.showPrimaryInsuredParty && ctx.showPrimaryRPPanel) {
    if (!form.PrimaryResponsiblePartyFirstName.trim()) e.PrimaryResponsiblePartyFirstName = "Required";
    if (!form.PrimaryResponsiblePartyLastName.trim()) e.PrimaryResponsiblePartyLastName = "Required";
    if (!form.PrimaryResponsiblePartyGender) e.PrimaryResponsiblePartyGender = "Required";
    if (!form.PrimaryResponsiblePartyDOBMonth || !form.PrimaryResponsiblePartyDOBDay || !form.PrimaryResponsiblePartyDOBYear) e.PrimaryResponsiblePartyDOB = "Full DOB required";
    validateAddr(f, "PrimaryResponsibleParty_", false, e);
  }
  if (form.HasSecondaryInsuranceYesNo === "1" && (!form.SecondaryInsID || Number(form.SecondaryInsID) <= 0)) e.SecondaryInsID = "Secondary insurance required when enabled";
  if (form.HasSecondaryInsuranceYesNo === "1" && ctx.showSecondaryRPPanel) {
    if (!form.SecondaryResponsiblePartyFirstName.trim()) e.SecondaryResponsiblePartyFirstName = "Required";
    if (!form.SecondaryResponsiblePartyLastName.trim()) e.SecondaryResponsiblePartyLastName = "Required";
    if (!form.SecondaryResponsiblePartyGender) e.SecondaryResponsiblePartyGender = "Required";
    if (!form.SecondaryResponsiblePartyDOBMonth || !form.SecondaryResponsiblePartyDOBDay || !form.SecondaryResponsiblePartyDOBYear) e.SecondaryResponsiblePartyDOB = "Full DOB required";
    validateAddr(f, "SecondaryResponsibleParty_", false, e);
  }
  if (ctx.showAuthDetails && (!form.countAuthorized.trim() || !/^\d{1,4}$/.test(form.countAuthorized))) e.countAuthorized = "Must be a number up to 4 digits";
  if (ctx.showPrescriptionDetails) {
    if (!form.prescriptionStartDate.trim()) e.prescriptionStartDate = "Required";
    if (!form.prescriptionEndDate.trim()) e.prescriptionEndDate = "Required";
    if (form.prescriptionStartDate && form.prescriptionEndDate) {
      const s = new Date(form.prescriptionStartDate),
        en = new Date(form.prescriptionEndDate);
      if (!isNaN(s.getTime()) && !isNaN(en.getTime()) && en < s) e.prescriptionEndDate = "End must be after start";
    }
  }
  return e;
}

// ─── Design Primitives ────────────────────────────────────────────────────────

const inputBase: React.CSSProperties = {
  width: "100%",
  height: 42,
  padding: "0 13px",
  borderRadius: 8,
  border: `1.5px solid ${C.border}`,
  fontSize: 13.5,
  color: C.text,
  background: C.surfaceRaised,
  fontFamily: "'DM Sans',sans-serif",
};
const selectBase: React.CSSProperties = {
  ...inputBase,
  cursor: "pointer",
  appearance: "none" as const,
  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6'%3E%3Cpath d='M1 1l4 4 4-4' stroke='%235F6E92' stroke-width='1.5' fill='none' stroke-linecap='round'/%3E%3C/svg%3E")`,
  backgroundRepeat: "no-repeat",
  backgroundPosition: "right 12px center",
  paddingRight: 32,
};
const EB = `1.5px solid ${C.error}`;
const NB = `1.5px solid ${C.border}`;

// Field wrapper
const F = ({ label, required, error, children, span }: { label: string; required?: boolean; error?: string; children: React.ReactNode; span?: number }) => (
  <div style={{ gridColumn: span ? `span ${span}` : undefined }}>
    <div
      style={{ marginBottom: 5, fontSize: 11.5, fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase" as const, color: error ? C.error : C.textMid, fontFamily: "'DM Sans',sans-serif" }}
    >
      {label}
      {required && <span style={{ color: C.primary, marginLeft: 2 }}>*</span>}
    </div>
    {children}
    {error && <div style={{ marginTop: 4, fontSize: 11.5, color: C.error }}>{error}</div>}
  </div>
);

// Text input
const TI = ({
  value,
  onChange,
  placeholder,
  maxLength,
  type = "text",
  err,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  maxLength?: number;
  type?: string;
  err?: boolean;
}) => <input style={{ ...inputBase, border: err ? EB : NB }} type={type} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} maxLength={maxLength} />;

// Select
const Sel = ({
  value,
  onChange,
  options,
  emptyOption = true,
  err,
  placeholder,
}: {
  value: string;
  onChange: (v: string) => void;
  options: [string | number, string][];
  emptyOption?: boolean;
  err?: boolean;
  placeholder?: string;
}) => (
  <select style={{ ...selectBase, border: err ? EB : NB, color: value ? C.text : C.textSoft }} value={value} onChange={(e) => onChange(e.target.value)}>
    {emptyOption && <option value="">{placeholder || "Select…"}</option>}
    {options.map(([v, d]) => (
      <option key={v} value={String(v)}>
        {d}
      </option>
    ))}
  </select>
);

// Phone group
const Phone = ({ ac, n1, n2, onAC, onN1, onN2, err }: { ac: string; n1: string; n2: string; onAC: (v: string) => void; onN1: (v: string) => void; onN2: (v: string) => void; err?: boolean }) => (
  <div style={{ display: "flex", gap: 5, alignItems: "center" }}>
    {(
      [
        [ac, onAC, 3, 52, "___"],
        [n1, onN1, 3, 52, "___"],
        [n2, onN2, 4, 68, "____"],
      ] as [string, (v: string) => void, number, number, string][]
    ).map(([val, fn, max, w, ph], i) => (
      <span key={i} style={{ display: "contents" }}>
        {i > 0 && <span style={{ color: C.textSoft, fontSize: 14, userSelect: "none" as const }}>–</span>}
        <input
          style={{ ...inputBase, width: w, flex: "none", textAlign: "center" as const, border: err ? EB : NB }}
          value={val}
          onChange={(e) => fn(e.target.value.replace(/\D/g, "").slice(0, max))}
          placeholder={ph}
          maxLength={max}
        />
      </span>
    ))}
  </div>
);

// DOB group
const DOB = ({ m, d, y, onM, onD, onY, err }: { m: string; d: string; y: string; onM: (v: string) => void; onD: (v: string) => void; onY: (v: string) => void; err?: boolean }) => (
  <div style={{ display: "flex", gap: 5, alignItems: "center" }}>
    <input
      style={{ ...inputBase, width: 52, textAlign: "center" as const, border: err ? EB : NB }}
      value={m}
      onChange={(e) => onM(e.target.value.replace(/\D/g, "").slice(0, 2))}
      placeholder="MM"
      maxLength={2}
    />
    <span style={{ color: C.textSoft, fontSize: 15 }}>/</span>
    <input
      style={{ ...inputBase, width: 52, textAlign: "center" as const, border: err ? EB : NB }}
      value={d}
      onChange={(e) => onD(e.target.value.replace(/\D/g, "").slice(0, 2))}
      placeholder="DD"
      maxLength={2}
    />
    <span style={{ color: C.textSoft, fontSize: 15 }}>/</span>
    <input
      style={{ ...inputBase, width: 76, textAlign: "center" as const, border: err ? EB : NB }}
      value={y}
      onChange={(e) => onY(e.target.value.replace(/\D/g, "").slice(0, 4))}
      placeholder="YYYY"
      maxLength={4}
    />
  </div>
);

// Radio pills
const RadioPill = ({ value, onChange, options }: { value: string; onChange: (v: string) => void; options: [string, string][] }) => (
  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" as const }}>
    {options.map(([v, l]) => {
      const active = value === v;
      return (
        <button
          key={v}
          type="button"
          onClick={() => onChange(v)}
          style={{
            height: 38,
            padding: "0 16px",
            borderRadius: 8,
            cursor: "pointer",
            fontSize: 13,
            fontFamily: "'DM Sans',sans-serif",
            fontWeight: active ? 600 : 400,
            transition: "all 0.14s",
            border: `1.5px solid ${active ? C.primary : C.border}`,
            background: active ? C.primaryLight : "transparent",
            color: active ? C.primary : C.textMid,
          }}
        >
          {l}
        </button>
      );
    })}
  </div>
);

// Toggle
const Toggle = ({ checked, onChange, label }: { checked: boolean; onChange: (v: boolean) => void; label: string }) => (
  <label style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer", userSelect: "none" as const }}>
    <div
      onClick={() => onChange(!checked)}
      style={{ width: 40, height: 22, borderRadius: 11, background: checked ? C.primary : C.border, position: "relative", transition: "background 0.2s", flexShrink: 0 }}
    >
      <div
        style={{ position: "absolute", top: 3, left: checked ? 19 : 3, width: 16, height: 16, borderRadius: 8, background: "#fff", transition: "left 0.18s", boxShadow: "0 1px 3px rgba(0,0,0,0.3)" }}
      />
    </div>
    <span style={{ fontSize: 13.5, color: C.textMid, fontFamily: "'DM Sans',sans-serif" }}>{label}</span>
  </label>
);

// Grid
const G = ({ cols = 3, children, gap = 14 }: { cols?: number; children: React.ReactNode; gap?: number }) => (
  <div style={{ display: "grid", gridTemplateColumns: `repeat(${cols},1fr)`, gap }}>{children}</div>
);

// Section header — left accent bar style
const SH = ({ title, subtitle, icon }: { title: string; subtitle?: string; icon?: string }) => (
  <div style={{ display: "flex", gap: 14, alignItems: "flex-start", marginBottom: 20, paddingBottom: 16, borderBottom: `1px solid ${C.border}` }}>
    {icon && (
      <div style={{ width: 36, height: 36, borderRadius: 8, background: C.primaryLight, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, flexShrink: 0, marginTop: 1 }}>
        {icon}
      </div>
    )}
    <div>
      <h3 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: C.text, fontFamily: "'Sora',sans-serif" }}>{title}</h3>
      {subtitle && <p style={{ margin: "3px 0 0", fontSize: 13, color: C.textSoft, fontFamily: "'DM Sans',sans-serif" }}>{subtitle}</p>}
    </div>
  </div>
);

// Card
const Card = ({ children, style }: { children: React.ReactNode; style?: React.CSSProperties }) => (
  <div style={{ background: C.surface, borderRadius: 14, border: `1px solid ${C.border}`, padding: "24px 28px", marginBottom: 16, boxShadow: "0 4px 20px rgba(0,0,0,0.15)", ...style }}>{children}</div>
);

// Address panel
interface AddrProps {
  prefix: string;
  data: Record<string, string>;
  onChange: (k: string, v: string) => void;
  a1Label?: string;
  errors?: FormErrors;
}
const AddrPanel = ({ prefix, data, onChange, a1Label = "Street Address", errors = {} }: AddrProps) => {
  const country = data[`${prefix}Country`] || "US";
  const isUSCA = country === "US" || country === "CA";
  const stateOpts = country === "CA" ? CA_PROVINCES : US_STATES;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <G cols={3}>
        <F label={a1Label} required error={errors[`${prefix}Address1`]} span={2}>
          <TI value={data[`${prefix}Address1`] || ""} onChange={(v) => onChange(`${prefix}Address1`, v)} maxLength={64} placeholder="123 Main St" err={!!errors[`${prefix}Address1`]} />
        </F>
        <F label="Unit / Suite">
          <TI value={data[`${prefix}Address2`] || ""} onChange={(v) => onChange(`${prefix}Address2`, v)} maxLength={64} placeholder="Apt 4B" />
        </F>
      </G>
      <G cols={3}>
        <F label="City" required error={errors[`${prefix}City`]}>
          <TI value={data[`${prefix}City`] || ""} onChange={(v) => onChange(`${prefix}City`, v)} maxLength={64} placeholder="City" err={!!errors[`${prefix}City`]} />
        </F>
        <F label="State / Province" required={isUSCA} error={errors[`${prefix}State`] || errors[`${prefix}StateText`]}>
          {isUSCA ? (
            <Sel value={data[`${prefix}State`] || ""} onChange={(v) => onChange(`${prefix}State`, v)} options={stateOpts} err={!!errors[`${prefix}State`]} />
          ) : (
            <TI value={data[`${prefix}StateText`] || ""} onChange={(v) => onChange(`${prefix}StateText`, v)} maxLength={45} err={!!errors[`${prefix}StateText`]} />
          )}
        </F>
        <F label="Postal Code" required={isUSCA} error={errors[`${prefix}PostalCode`]}>
          <TI value={data[`${prefix}PostalCode`] || ""} onChange={(v) => onChange(`${prefix}PostalCode`, v)} maxLength={10} placeholder="00000" err={!!errors[`${prefix}PostalCode`]} />
        </F>
      </G>
      <G cols={3}>
        <F label="Country">
          <Sel
            value={country}
            onChange={(v) => {
              onChange(`${prefix}Country`, v);
              onChange(`${prefix}State`, "");
              onChange(`${prefix}StateText`, "");
            }}
            options={COUNTRIES}
            emptyOption={false}
          />
        </F>
      </G>
    </div>
  );
};

// Error banner
const ErrorBanner = ({ errors }: { errors: FormErrors }) => {
  const msgs = [...new Set(Object.values(errors).filter(Boolean) as string[])];
  if (!msgs.length) return null;
  return (
    <div style={{ background: C.errorLight, border: `1.5px solid ${C.errorBorder}`, borderRadius: 12, padding: "14px 18px", marginBottom: 20, display: "flex", gap: 12, alignItems: "flex-start" }}>
      <span style={{ fontSize: 18, flexShrink: 0 }}>⚠️</span>
      <div>
        <p style={{ margin: "0 0 6px", fontWeight: 700, color: C.error, fontSize: 13, fontFamily: "'DM Sans',sans-serif" }}>Please fix the following before saving:</p>
        <ul style={{ margin: 0, padding: "0 0 0 16px", color: C.error, fontSize: 12, lineHeight: 1.8, fontFamily: "'DM Sans',sans-serif" }}>
          {msgs.map((m) => (
            <li key={m}>{m}</li>
          ))}
        </ul>
      </div>
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────

interface QuickAddPatientFormProps {
  onSubmit?: (data: QuickAddPatientPayload) => void;
  onCancel?: () => void;
  practiceName?: string;
}

export default function QuickAddPatientForm({ onSubmit, onCancel, practiceName = "Your Practice" }: QuickAddPatientFormProps) {
  const [form, setForm] = useState<QuickAddPatientPayload>(initialState);
  const [errors, setErrors] = useState<FormErrors>({});
  const [submitted, setSubmitted] = useState(false);
  const [done, setDone] = useState(false);
  const [selectedFacilities, setSelectedFacilities] = useState<number[]>([]);
  const [primaryInsRecord, setPrimaryInsRecord] = useState<(typeof MOCK_INSURANCES)[0] | null>(null);

  const [showWorkerComp, setShowWorkerComp] = useState(false);
  const [showPrimaryIns, setShowPrimaryIns] = useState(true);
  const [showPrimaryRP, setShowPrimaryRP] = useState(false);
  const [showSecondaryRP, setShowSecondaryRP] = useState(false);
  const [showGuarantor, setShowGuarantor] = useState(false);
  const [showAuth, setShowAuth] = useState(false);
  const [showRx, setShowRx] = useState(false);
  const [showAccidentDate, setShowAccidentDate] = useState(false);
  const [showAccidentState, setShowAccidentState] = useState(false);

  const ctx: ValidationCtx = {
    showGuarantorPanel: showGuarantor,
    showWorkerComp,
    showPrimaryInsuredParty: showPrimaryIns,
    showPrimaryRPPanel: showPrimaryRP,
    showSecondaryRPPanel: showSecondaryRP,
    showAuthDetails: showAuth,
    showPrescriptionDetails: showRx,
  };

  const setField = useCallback(
    (key: keyof QuickAddPatientPayload, val: unknown) => {
      setForm((prev) => {
        const next = { ...prev, [key]: val };
        if (submitted) setErrors(validate(next, ctx));
        return next;
      });
    },
    [submitted, showGuarantor, showWorkerComp, showPrimaryIns, showPrimaryRP, showSecondaryRP, showAuth, showRx],
  );

  const addrChange = useCallback(
    (key: string, val: string) => {
      setForm((prev) => {
        const next = { ...prev, [key]: val } as QuickAddPatientPayload;
        if (submitted) setErrors(validate(next, ctx));
        return next;
      });
    },
    [submitted, showGuarantor, showWorkerComp, showPrimaryIns, showPrimaryRP, showSecondaryRP, showAuth, showRx],
  );

  const updateCtx = (updates: Partial<QuickAddPatientPayload>, newCtx: ValidationCtx) => {
    setForm((prev) => {
      const next = { ...prev, ...updates };
      if (submitted) setErrors(validate(next, newCtx));
      return next;
    });
  };

  const handlePrimaryIns = (insId: string) => {
    const rec = MOCK_INSURANCES.find((i) => String(i.InsID) === insId) || null;
    setPrimaryInsRecord(rec);
    const isWC = rec?.InsTypeID === 7,
      isSP = rec?.InsID === 1;
    setShowWorkerComp(isWC);
    setShowPrimaryIns(!isWC && !isSP);
    updateCtx({ PrimaryInsID: insId ? Number(insId) : "", PrimaryInsurance: rec?.InsName || "" }, { ...ctx, showWorkerComp: isWC, showPrimaryInsuredParty: !isWC && !isSP });
  };
  const handleCause = (val: string) => {
    setShowAccidentDate(["AA", "AF", "EM", "OA"].includes(val));
    setShowAccidentState(["AA", "AF"].includes(val));
    setField("RelatedCause", val);
  };
  const handleIsGuarantor = (checked: boolean) => {
    setShowGuarantor(!checked);
    updateCtx({ PrimaryIsGuarantor: checked ? "on" : "off" }, { ...ctx, showGuarantorPanel: !checked });
  };
  const handleAuth = (val: string) => {
    setShowAuth(val === "1");
    updateCtx({ primaryInsuranceAuthorizationRequiredYesNo: val }, { ...ctx, showAuthDetails: val === "1" });
  };
  const handleRx = (val: string) => {
    setShowRx(val === "1");
    updateCtx({ prescriptionInformationYesNo: val }, { ...ctx, showPrescriptionDetails: val === "1" });
  };
  const handlePrimaryIP = (val: string) => {
    const show = val !== "Self" && val !== "1";
    setShowPrimaryRP(show);
    updateCtx({ PrimaryInsuredParty: val }, { ...ctx, showPrimaryRPPanel: show });
  };
  const handleSecondaryIP = (val: string) => {
    const show = val !== "Self" && val !== "1";
    setShowSecondaryRP(show);
    updateCtx({ SecondaryInsuredParty: val }, { ...ctx, showSecondaryRPPanel: show });
  };
  const toggleFacility = (id: number) => {
    setSelectedFacilities((prev) => {
      const next = prev.includes(id) ? prev.filter((f) => f !== id) : [...prev, id];
      setForm((f) => ({ ...f, Facilities: next.join(";") }));
      return next;
    });
  };

  const handleSubmit = () => {
    setSubmitted(true);
    const errs = validate(form, ctx);
    setErrors(errs);
    if (Object.keys(errs).length > 0) {
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }
    const payload: QuickAddPatientPayload = { ...form, Sex: form.Sex.substring(0, 1) };
    onSubmit?.(payload);
    setDone(true);
  };

  const fd = form as unknown as Record<string, string>;
  const e = errors;

  const btnPrimary: React.CSSProperties = {
    height: 46,
    padding: "0 32px",
    borderRadius: 10,
    border: "none",
    cursor: "pointer",
    background: `linear-gradient(135deg, ${C.primary} 0%, ${C.primaryDark} 100%)`,
    color: "#fff",
    fontSize: 14,
    fontWeight: 700,
    fontFamily: "'DM Sans',sans-serif",
    boxShadow: `0 4px 16px ${C.primaryGlow}`,
    letterSpacing: "0.01em",
  };
  const btnSecondary: React.CSSProperties = {
    height: 46,
    padding: "0 24px",
    borderRadius: 10,
    cursor: "pointer",
    border: `1.5px solid ${C.border}`,
    background: "transparent",
    color: C.textMid,
    fontSize: 14,
    fontFamily: "'DM Sans',sans-serif",
  };

  // ── Completion screen ─────────────────────────────────────────────────────

  if (done) {
    return (
      <>
        <style>{GLOBAL_STYLE}</style>
        <div style={{ minHeight: "100vh", background: C.bg, display: "flex", alignItems: "center", justifyContent: "center", padding: "40px 20px" }}>
          <div style={{ background: C.surface, borderRadius: 20, border: `1px solid ${C.border}`, padding: "48px 52px", maxWidth: 480, textAlign: "center", boxShadow: "0 8px 40px rgba(0,0,0,0.3)" }}>
            <div style={{ width: 68, height: 68, borderRadius: 34, background: C.successLight, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 24px", fontSize: 32 }}>
              ✅
            </div>
            <h2 style={{ margin: "0 0 12px", fontSize: 24, fontWeight: 700, color: C.text, fontFamily: "'Sora',sans-serif" }}>You're all set!</h2>
            <p style={{ margin: "0 0 28px", fontSize: 15, color: C.textMid, lineHeight: 1.7, fontFamily: "'DM Sans',sans-serif" }}>
              Your registration has been submitted. We'll reach out soon to confirm your appointment.
            </p>
            <div style={{ padding: "14px 20px", background: C.primaryLight, borderRadius: 10, fontSize: 14, color: C.primary, fontWeight: 500, fontFamily: "'DM Sans',sans-serif" }}>
              Welcome, {form.FirstName}! We look forward to seeing you.
            </div>
          </div>
        </div>
      </>
    );
  }

  // ── Main render ───────────────────────────────────────────────────────────

  return (
    <>
      <style>{GLOBAL_STYLE}</style>
      <div style={{ minHeight: "100vh", background: C.bg, fontFamily: "'DM Sans',sans-serif" }}>
        {/* Top bar */}
        <div
          style={{
            background: C.surface,
            borderBottom: `1px solid ${C.border}`,
            padding: "0 40px",
            height: 60,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            position: "sticky",
            top: 0,
            zIndex: 100,
            boxShadow: "0 2px 12px rgba(0,0,0,0.25)",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div
              style={{
                width: 30,
                height: 30,
                borderRadius: 7,
                background: `linear-gradient(135deg,${C.primary},${C.primaryDark})`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: 15,
              }}
            >
              🏥
            </div>
            <span style={{ fontSize: 15, fontWeight: 700, color: C.text, fontFamily: "'Sora',sans-serif" }}>{practiceName}</span>
          </div>
          <span style={{ fontSize: 12, color: C.textSoft, letterSpacing: "0.05em", textTransform: "uppercase" as const }}>Patient Intake Form</span>
        </div>

        {/* Hero banner */}
        <div
          style={{
            background: `linear-gradient(130deg, #202C50 0%, #1A2340 60%, #16203A 100%)`,
            borderBottom: `1px solid ${C.border}`,
            padding: "36px 40px 40px",
            position: "relative",
            overflow: "hidden",
          }}
        >
          {/* decorative orbs */}
          <div style={{ position: "absolute", top: -80, right: -80, width: 260, height: 260, borderRadius: 130, background: `radial-gradient(circle, ${C.primaryGlow} 0%, transparent 70%)` }} />
          <div style={{ position: "absolute", bottom: -60, left: 60, width: 180, height: 180, borderRadius: 90, background: `radial-gradient(circle, rgba(94,120,238,0.08) 0%, transparent 70%)` }} />
          <div style={{ maxWidth: 760, margin: "0 auto", position: "relative" }}>
            <p style={{ margin: "0 0 6px", fontSize: 11, fontWeight: 600, color: C.primary, letterSpacing: "0.12em", textTransform: "uppercase" as const }}>Patient Registration</p>
            <h1 style={{ margin: "0 0 10px", fontSize: 26, fontWeight: 700, color: C.text, fontFamily: "'Sora',sans-serif", lineHeight: 1.25 }}>Welcome! Let's get you registered.</h1>
            <p style={{ margin: 0, fontSize: 14, color: C.textMid, maxWidth: 460, lineHeight: 1.65 }}>
              Please complete all sections below. Your information is secure and only used to coordinate your care.
            </p>
          </div>
        </div>

        {/* Form body */}
        <div style={{ maxWidth: 820, margin: "0 auto", padding: "32px 40px 80px" }}>
          <ErrorBanner errors={e} />

          {/* ── Personal Information ── */}
          <Card>
            <SH title="Personal Information" subtitle="Basic details for your patient record." icon="👤" />
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              <G cols={3}>
                <F label="First Name" required error={e.FirstName}>
                  <TI value={form.FirstName} onChange={(v) => setField("FirstName", v)} maxLength={64} placeholder="Jane" err={!!e.FirstName} />
                </F>
                <F label="Middle Name">
                  <TI value={form.MiddleName} onChange={(v) => setField("MiddleName", v)} maxLength={64} placeholder="Marie" />
                </F>
                <F label="Last Name" required error={e.LastName}>
                  <TI value={form.LastName} onChange={(v) => setField("LastName", v)} maxLength={64} placeholder="Doe" err={!!e.LastName} />
                </F>
              </G>
              <G cols={3}>
                <F label="Preferred Name">
                  <TI value={form.preferredName} onChange={(v) => setField("preferredName", v)} maxLength={64} placeholder="Goes by…" />
                </F>
                <F label="Suffix">
                  <TI value={form.SuffixName} onChange={(v) => setField("SuffixName", v)} maxLength={10} placeholder="Jr., Sr.…" />
                </F>
              </G>
              <G cols={2}>
                <F label="Date of Birth" required error={e.DOB}>
                  <DOB m={form.DOBMonth} d={form.DOBDay} y={form.DOBYear} onM={(v) => setField("DOBMonth", v)} onD={(v) => setField("DOBDay", v)} onY={(v) => setField("DOBYear", v)} err={!!e.DOB} />
                </F>
                <F label="Sex" required error={e.Sex}>
                  <Sel
                    value={form.Sex}
                    onChange={(v) => setField("Sex", v)}
                    err={!!e.Sex}
                    options={[
                      ["M", "Male"],
                      ["F", "Female"],
                      ["U", "Unknown / Prefer not to say"],
                    ]}
                  />
                </F>
              </G>
              <G cols={2}>
                <F label="Pronouns">
                  <Sel
                    value={form.pronounsId}
                    onChange={(v) => setField("pronounsId", v)}
                    options={[
                      ["1", "He / Him"],
                      ["2", "She / Her"],
                      ["3", "They / Them"],
                      ["4", "Other"],
                    ]}
                  />
                </F>
                <F label="Pronoun (Self Describe)">
                  <TI value={form.pronounSelfDescribe} onChange={(v) => setField("pronounSelfDescribe", v)} placeholder="Describe…" />
                </F>
              </G>
              <G cols={2}>
                <F label="Gender Identity">
                  <Sel
                    value={form.genderIdentityId}
                    onChange={(v) => setField("genderIdentityId", v)}
                    options={[
                      ["1", "Male"],
                      ["2", "Female"],
                      ["3", "Transgender Male"],
                      ["4", "Transgender Female"],
                      ["5", "Non-binary"],
                      ["6", "Other"],
                    ]}
                  />
                </F>
                <F label="Gender (Self Describe)">
                  <TI value={form.genderSelfDescribed} onChange={(v) => setField("genderSelfDescribed", v)} placeholder="Describe…" />
                </F>
              </G>
            </div>
          </Card>

          {/* ── Home Address ── */}
          <Card>
            <SH title="Home Address" subtitle="Used for billing and correspondence." icon="🏠" />
            <AddrPanel prefix="" data={fd} onChange={addrChange} errors={e} />
          </Card>

          {/* ── Contact ── */}
          <Card>
            <SH title="Contact Information" icon="📞" />
            {(e.phone || e.homePhone || e.mobilePhone) && (
              <div style={{ background: C.errorLight, border: `1px solid ${C.errorBorder}`, borderRadius: 8, padding: "9px 13px", marginBottom: 16, fontSize: 12.5, color: C.error }}>
                {e.phone || e.homePhone || e.mobilePhone}
              </div>
            )}
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              {/* Phone rows in contained blocks */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                <div style={{ background: C.surfaceRaised, borderRadius: 10, padding: "16px 18px", border: `1px solid ${C.border}` }}>
                  <p style={{ margin: "0 0 10px", fontSize: 11, fontWeight: 700, color: C.primary, textTransform: "uppercase" as const, letterSpacing: "0.08em" }}>Home Phone</p>
                  <F label="Number" error={e.homePhone}>
                    <Phone
                      ac={form.AreaCode}
                      n1={form.Number1}
                      n2={form.Number2}
                      onAC={(v) => setField("AreaCode", v)}
                      onN1={(v) => setField("Number1", v)}
                      onN2={(v) => setField("Number2", v)}
                      err={!!e.homePhone || !!e.phone}
                    />
                  </F>
                  <div style={{ marginTop: 10 }}>
                    <F label="Reminders">
                      <Sel
                        value={form.AppointmentRemindersHome}
                        onChange={(v) => setField("AppointmentRemindersHome", v)}
                        options={[
                          ["None", "None"],
                          ["1", "Voice Calls"],
                          ["2", "Text Message"],
                        ]}
                        emptyOption={false}
                      />
                    </F>
                  </div>
                </div>
                <div style={{ background: C.surfaceRaised, borderRadius: 10, padding: "16px 18px", border: `1px solid ${C.border}` }}>
                  <p style={{ margin: "0 0 10px", fontSize: 11, fontWeight: 700, color: C.primary, textTransform: "uppercase" as const, letterSpacing: "0.08em" }}>Mobile Phone</p>
                  <F label="Number" error={e.mobilePhone}>
                    <Phone
                      ac={form.SecondaryAreaCode}
                      n1={form.SecondaryNumber1}
                      n2={form.SecondaryNumber2}
                      onAC={(v) => setField("SecondaryAreaCode", v)}
                      onN1={(v) => setField("SecondaryNumber1", v)}
                      onN2={(v) => setField("SecondaryNumber2", v)}
                      err={!!e.mobilePhone || !!e.phone}
                    />
                  </F>
                  <div style={{ marginTop: 10 }}>
                    <F label="Reminders">
                      <Sel
                        value={form.AppointmentRemindersMobile}
                        onChange={(v) => setField("AppointmentRemindersMobile", v)}
                        options={[
                          ["None", "None"],
                          ["1", "Voice Calls"],
                          ["2", "Text Message"],
                        ]}
                        emptyOption={false}
                      />
                    </F>
                  </div>
                </div>
              </div>
              <G cols={3}>
                <F label="Email Address" error={e.emailReminder} span={2}>
                  <TI value={form.emailReminder} onChange={(v) => setField("emailReminder", v)} type="email" placeholder="jane@example.com" err={!!e.emailReminder} />
                </F>
                <F label="Email Reminders">
                  <Sel
                    value={form.AppointmentReminders}
                    onChange={(v) => setField("AppointmentReminders", v)}
                    options={[
                      ["None", "None"],
                      ["1", "Email Reminders"],
                    ]}
                    emptyOption={false}
                  />
                </F>
              </G>
              <F label="Primary Contact Method">
                <RadioPill
                  value={form.PrimaryContact}
                  onChange={(v) => setField("PrimaryContact", v)}
                  options={[
                    ["Home", "Home Phone"],
                    ["Mobile", "Mobile"],
                    ["Email", "Email"],
                  ]}
                />
              </F>
            </div>
          </Card>

          {/* ── Case Details ── */}
          <Card>
            <SH title="Case Details" subtitle="If your visit is related to an accident or specific event." icon="📋" />
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              <G cols={2}>
                <F label="Case Title">
                  <TI value={form.caseTitle} onChange={(v) => setField("caseTitle", v)} maxLength={100} placeholder="Optional label" />
                </F>
                <F label="Related Cause">
                  <Sel value={form.RelatedCause} onChange={handleCause} options={RELATED_CAUSES.filter((r) => r[0]) as [string, string][]} />
                </F>
              </G>
              {(showAccidentDate || showAccidentState) && (
                <G cols={2}>
                  {showAccidentDate && (
                    <F label="Accident Date">
                      <TI value={form.AccidentDate} onChange={(v) => setField("AccidentDate", v)} placeholder="MM/DD/YYYY" />
                    </F>
                  )}
                  {showAccidentState && (
                    <F label="Accident State">
                      <Sel value={form.AccidentStateProvince} onChange={(v) => setField("AccidentStateProvince", v)} options={US_STATES.slice(0, 50)} />
                    </F>
                  )}
                </G>
              )}
              <F label="Additional Notes">
                <textarea
                  value={form.additionalInformation}
                  onChange={(ev) => setField("additionalInformation", ev.target.value)}
                  maxLength={500}
                  rows={3}
                  placeholder="Anything else we should know…"
                  style={{ ...inputBase, height: "auto", padding: "10px 13px", resize: "vertical" as const, border: NB, lineHeight: 1.6 }}
                />
              </F>
            </div>
          </Card>

          {/* ── Primary Insurance ── */}
          <Card>
            <SH title="Primary Insurance" subtitle="The insurance plan to be billed first." icon="🏥" />
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              <F label="Insurance Provider" required error={e.PrimaryInsID}>
                <Sel
                  value={String(form.PrimaryInsID)}
                  onChange={handlePrimaryIns}
                  err={!!e.PrimaryInsID}
                  options={MOCK_INSURANCES.map((i) => [String(i.InsID), i.InsName] as [string, string])}
                  placeholder="Choose your plan…"
                />
              </F>
              <G cols={2}>
                <F label={primaryInsRecord?.InsTypeID === 3 ? "Medicare Beneficiary ID" : "Policy Subscriber ID"}>
                  <TI value={form.PrimaryPolicySubscriberNumber} onChange={(v) => setField("PrimaryPolicySubscriberNumber", v)} maxLength={92} placeholder="ID number" />
                </F>
                <F label="Policy Group Number">
                  <TI value={form.PrimaryPolicyGroupNumber} onChange={(v) => setField("PrimaryPolicyGroupNumber", v)} maxLength={92} placeholder="Group number" />
                </F>
              </G>
              {showPrimaryIns && (
                <F label="Who is the insured party?">
                  <Sel value={String(form.PrimaryInsuredParty)} onChange={handlePrimaryIP} options={INSURED_PARTY_OPTIONS} emptyOption={false} />
                </F>
              )}
              <Toggle checked={form.PrimaryIsGuarantor === "on"} onChange={handleIsGuarantor} label="I am responsible for this account (I am the guarantor)" />
            </div>

            {/* Worker's Comp */}
            {showWorkerComp && (
              <div style={{ marginTop: 20, padding: "20px 22px", background: C.surfaceRaised, borderRadius: 10, border: `1px solid ${C.border}` }}>
                <p style={{ margin: "0 0 16px", fontSize: 12, fontWeight: 700, color: C.primary, textTransform: "uppercase" as const, letterSpacing: "0.08em" }}>Worker's Compensation</p>
                <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                  <G cols={2}>
                    <F label="Claim Number" required error={e.WComp_ClaimNumber}>
                      <TI value={form.WComp_ClaimNumber} onChange={(v) => setField("WComp_ClaimNumber", v)} err={!!e.WComp_ClaimNumber} placeholder="Claim #" />
                    </F>
                    <F label="Employer Name" required error={e.WComp_FirstName}>
                      <TI value={form.WComp_FirstName} onChange={(v) => setField("WComp_FirstName", v)} err={!!e.WComp_FirstName} />
                    </F>
                  </G>
                  <F label="Employer Phone" required error={e.WComp_Phone}>
                    <Phone
                      ac={form.WComp_PhoneAreaCode}
                      n1={form.WComp_PhoneLocalNumber1}
                      n2={form.WComp_PhoneLocalNumber2}
                      onAC={(v) => setField("WComp_PhoneAreaCode", v)}
                      onN1={(v) => setField("WComp_PhoneLocalNumber1", v)}
                      onN2={(v) => setField("WComp_PhoneLocalNumber2", v)}
                      err={!!e.WComp_Phone}
                    />
                  </F>
                  <AddrPanel prefix="WComp_" data={fd} onChange={addrChange} a1Label="Employer's Street Address" errors={e} />
                </div>
              </div>
            )}

            {/* Primary Responsible Party */}
            {showPrimaryIns && showPrimaryRP && (
              <div style={{ marginTop: 20, padding: "20px 22px", background: C.surfaceRaised, borderRadius: 10, border: `1px solid ${C.border}` }}>
                <p style={{ margin: "0 0 16px", fontSize: 12, fontWeight: 700, color: C.primary, textTransform: "uppercase" as const, letterSpacing: "0.08em" }}>Primary Responsible Party</p>
                <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                  <G cols={3}>
                    <F label="First Name" required error={e.PrimaryResponsiblePartyFirstName}>
                      <TI value={form.PrimaryResponsiblePartyFirstName} onChange={(v) => setField("PrimaryResponsiblePartyFirstName", v)} err={!!e.PrimaryResponsiblePartyFirstName} />
                    </F>
                    <F label="Middle Name">
                      <TI value={form.PrimaryResponsiblePartyMiddleName} onChange={(v) => setField("PrimaryResponsiblePartyMiddleName", v)} />
                    </F>
                    <F label="Last Name" required error={e.PrimaryResponsiblePartyLastName}>
                      <TI value={form.PrimaryResponsiblePartyLastName} onChange={(v) => setField("PrimaryResponsiblePartyLastName", v)} err={!!e.PrimaryResponsiblePartyLastName} />
                    </F>
                  </G>
                  <G cols={2}>
                    <F label="Date of Birth" required error={e.PrimaryResponsiblePartyDOB}>
                      <DOB
                        m={form.PrimaryResponsiblePartyDOBMonth}
                        d={form.PrimaryResponsiblePartyDOBDay}
                        y={form.PrimaryResponsiblePartyDOBYear}
                        onM={(v) => setField("PrimaryResponsiblePartyDOBMonth", v)}
                        onD={(v) => setField("PrimaryResponsiblePartyDOBDay", v)}
                        onY={(v) => setField("PrimaryResponsiblePartyDOBYear", v)}
                        err={!!e.PrimaryResponsiblePartyDOB}
                      />
                    </F>
                    <F label="Gender" required error={e.PrimaryResponsiblePartyGender}>
                      <Sel
                        value={form.PrimaryResponsiblePartyGender}
                        onChange={(v) => setField("PrimaryResponsiblePartyGender", v)}
                        options={[
                          ["M", "Male"],
                          ["F", "Female"],
                        ]}
                        err={!!e.PrimaryResponsiblePartyGender}
                      />
                    </F>
                  </G>
                  <G cols={2}>
                    <F label="SSN">
                      <TI value={form.PrimaryResponsiblePartySSN} onChange={(v) => setField("PrimaryResponsiblePartySSN", v)} type="password" placeholder="•••–••–••••" />
                    </F>
                    <F label="Phone">
                      <Phone
                        ac={form.PrimaryResponsiblePartyPhoneAreaCode}
                        n1={form.PrimaryResponsiblePartyPhoneLocalNumber1}
                        n2={form.PrimaryResponsiblePartyPhoneLocalNumber2}
                        onAC={(v) => setField("PrimaryResponsiblePartyPhoneAreaCode", v)}
                        onN1={(v) => setField("PrimaryResponsiblePartyPhoneLocalNumber1", v)}
                        onN2={(v) => setField("PrimaryResponsiblePartyPhoneLocalNumber2", v)}
                      />
                    </F>
                  </G>
                  <AddrPanel prefix="PrimaryResponsibleParty_" data={fd} onChange={addrChange} errors={e} />
                </div>
              </div>
            )}
          </Card>

          {/* ── Guarantor ── */}
          {showGuarantor && (
            <Card>
              <SH title="Guarantor Information" subtitle="The person financially responsible for this account." icon="💼" />
              <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                <G cols={3}>
                  <F label="First Name" required error={e.guarantorFirstName}>
                    <TI value={form.guarantorFirstName} onChange={(v) => setField("guarantorFirstName", v)} err={!!e.guarantorFirstName} />
                  </F>
                  <F label="Middle Name">
                    <TI value={form.guarantorMiddleName} onChange={(v) => setField("guarantorMiddleName", v)} />
                  </F>
                  <F label="Last Name" required error={e.guarantorLastName}>
                    <TI value={form.guarantorLastName} onChange={(v) => setField("guarantorLastName", v)} err={!!e.guarantorLastName} />
                  </F>
                </G>
                <G cols={3}>
                  <F label="Gender" required error={e.guarantorGender}>
                    <Sel
                      value={form.guarantorGender}
                      onChange={(v) => setField("guarantorGender", v)}
                      options={[
                        ["M", "Male"],
                        ["F", "Female"],
                      ]}
                      err={!!e.guarantorGender}
                    />
                  </F>
                  <F label="Date of Birth" required error={e.guarantorDOB}>
                    <DOB
                      m={form.guarantorDOBMonth}
                      d={form.guarantorDOBDay}
                      y={form.guarantorDOBYear}
                      onM={(v) => setField("guarantorDOBMonth", v)}
                      onD={(v) => setField("guarantorDOBDay", v)}
                      onY={(v) => setField("guarantorDOBYear", v)}
                      err={!!e.guarantorDOB}
                    />
                  </F>
                  <F label="Relation to Patient" required error={e.guarantorPatientRelation}>
                    <Sel value={form.guarantorPatientRelation} onChange={(v) => setField("guarantorPatientRelation", v)} options={PATIENT_RELATION_OPTIONS} err={!!e.guarantorPatientRelation} />
                  </F>
                </G>
                <G cols={2}>
                  <F label="SSN">
                    <TI value={form.guarantorSSN} onChange={(v) => setField("guarantorSSN", v)} type="password" placeholder="•••–••–••••" />
                  </F>
                  <F label="Phone">
                    <Phone
                      ac={form.guarantorPhoneAreaCode}
                      n1={form.guarantorPhoneLocalNumber1}
                      n2={form.guarantorPhoneLocalNumber2}
                      onAC={(v) => setField("guarantorPhoneAreaCode", v)}
                      onN1={(v) => setField("guarantorPhoneLocalNumber1", v)}
                      onN2={(v) => setField("guarantorPhoneLocalNumber2", v)}
                    />
                  </F>
                </G>
                <AddrPanel prefix="Guarantor_" data={fd} onChange={addrChange} errors={e} />
              </div>
            </Card>
          )}

          {/* ── Secondary Insurance ── */}
          <Card>
            <SH title="Secondary Insurance" subtitle="Do you have a second insurance plan?" icon="📄" />
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              <F label="Do you have secondary insurance?">
                <RadioPill
                  value={form.HasSecondaryInsuranceYesNo}
                  onChange={(v) => setField("HasSecondaryInsuranceYesNo", v)}
                  options={[
                    ["1", "Yes"],
                    ["0", "No"],
                  ]}
                />
              </F>
              {form.HasSecondaryInsuranceYesNo === "1" && (
                <>
                  <F label="Insurance Provider" required error={e.SecondaryInsID}>
                    <Sel
                      value={String(form.SecondaryInsID)}
                      err={!!e.SecondaryInsID}
                      onChange={(v) => {
                        const rec = MOCK_INSURANCES.find((i) => String(i.InsID) === v);
                        updateCtx({ SecondaryInsID: v ? Number(v) : "", SecondaryInsurance: rec?.InsName || "" }, ctx);
                      }}
                      options={MOCK_INSURANCES.map((i) => [String(i.InsID), i.InsName] as [string, string])}
                    />
                  </F>
                  <G cols={2}>
                    <F label="Policy Subscriber ID">
                      <TI value={form.SecondaryPolicySubscriberNumber} onChange={(v) => setField("SecondaryPolicySubscriberNumber", v)} maxLength={92} />
                    </F>
                    <F label="Policy Group Number">
                      <TI value={form.SecondaryPolicyGroupNumber} onChange={(v) => setField("SecondaryPolicyGroupNumber", v)} maxLength={92} />
                    </F>
                  </G>
                  <F label="Who is the insured party?">
                    <Sel value={String(form.SecondaryInsuredParty)} onChange={handleSecondaryIP} options={INSURED_PARTY_OPTIONS} emptyOption={false} />
                  </F>
                  {showSecondaryRP && (
                    <div style={{ padding: "18px 20px", background: C.surfaceRaised, borderRadius: 10, border: `1px solid ${C.border}` }}>
                      <p style={{ margin: "0 0 14px", fontSize: 12, fontWeight: 700, color: C.primary, textTransform: "uppercase" as const, letterSpacing: "0.08em" }}>Secondary Responsible Party</p>
                      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                        <G cols={3}>
                          <F label="First Name" required error={e.SecondaryResponsiblePartyFirstName}>
                            <TI value={form.SecondaryResponsiblePartyFirstName} onChange={(v) => setField("SecondaryResponsiblePartyFirstName", v)} err={!!e.SecondaryResponsiblePartyFirstName} />
                          </F>
                          <F label="Middle Name">
                            <TI value={form.SecondaryResponsiblePartyMiddleName} onChange={(v) => setField("SecondaryResponsiblePartyMiddleName", v)} />
                          </F>
                          <F label="Last Name" required error={e.SecondaryResponsiblePartyLastName}>
                            <TI value={form.SecondaryResponsiblePartyLastName} onChange={(v) => setField("SecondaryResponsiblePartyLastName", v)} err={!!e.SecondaryResponsiblePartyLastName} />
                          </F>
                        </G>
                        <G cols={2}>
                          <F label="Date of Birth" required error={e.SecondaryResponsiblePartyDOB}>
                            <DOB
                              m={form.SecondaryResponsiblePartyDOBMonth}
                              d={form.SecondaryResponsiblePartyDOBDay}
                              y={form.SecondaryResponsiblePartyDOBYear}
                              onM={(v) => setField("SecondaryResponsiblePartyDOBMonth", v)}
                              onD={(v) => setField("SecondaryResponsiblePartyDOBDay", v)}
                              onY={(v) => setField("SecondaryResponsiblePartyDOBYear", v)}
                              err={!!e.SecondaryResponsiblePartyDOB}
                            />
                          </F>
                          <F label="Gender" required error={e.SecondaryResponsiblePartyGender}>
                            <Sel
                              value={form.SecondaryResponsiblePartyGender}
                              onChange={(v) => setField("SecondaryResponsiblePartyGender", v)}
                              options={[
                                ["M", "Male"],
                                ["F", "Female"],
                              ]}
                              err={!!e.SecondaryResponsiblePartyGender}
                            />
                          </F>
                        </G>
                        <AddrPanel prefix="SecondaryResponsibleParty_" data={fd} onChange={addrChange} errors={e} />
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </Card>

          {/* ── Care Team ── */}
          <Card>
            <SH title="Care Team" subtitle="Connect you with the right providers." icon="🩺" />
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              <G cols={2}>
                <F label="Physician" required error={e.PhysicianID}>
                  <Sel
                    value={String(form.PhysicianID)}
                    err={!!e.PhysicianID}
                    onChange={(v) => {
                      const rec = MOCK_PHYSICIANS.find((p) => String(p.PhysicianID) === v);
                      updateCtx({ PhysicianID: v ? Number(v) : "", Physician: rec ? `${rec.Name} (${rec.Identification})` : "" }, ctx);
                    }}
                    options={MOCK_PHYSICIANS.map((p) => [String(p.PhysicianID), p.Name] as [string, string])}
                    placeholder="Select physician…"
                  />
                </F>
                <F label="Therapist" required error={e.TherapistID}>
                  <Sel
                    value={String(form.TherapistID)}
                    err={!!e.TherapistID}
                    onChange={(v) => updateCtx({ TherapistID: v ? Number(v) : "" }, ctx)}
                    options={MOCK_THERAPISTS.map((t) => [String(t.TherapistID), t.Name] as [string, string])}
                    placeholder="Select therapist…"
                  />
                </F>
              </G>
              <G cols={2}>
                <F label="Referred by Physician?">
                  <RadioPill
                    value={form.IsReferredByPhysicianYesNo}
                    onChange={(v) => setField("IsReferredByPhysicianYesNo", v)}
                    options={[
                      ["1", "Yes"],
                      ["0", "No"],
                    ]}
                  />
                </F>
                {form.IsReferredByPhysicianYesNo === "1" && (
                  <F label="Referral Date">
                    <TI value={form.ReferralDate} onChange={(v) => setField("ReferralDate", v)} placeholder="MM/DD/YYYY" />
                  </F>
                )}
              </G>
              <F label="Preferred Location(s)">
                <div style={{ display: "flex", flexWrap: "wrap" as const, gap: 8, marginTop: 2 }}>
                  {MOCK_FACILITIES.map((f) => {
                    const sel = selectedFacilities.includes(f.value);
                    return (
                      <button
                        key={f.value}
                        type="button"
                        onClick={() => toggleFacility(f.value)}
                        style={{
                          height: 36,
                          padding: "0 14px",
                          borderRadius: 8,
                          cursor: "pointer",
                          fontSize: 13,
                          fontFamily: "'DM Sans',sans-serif",
                          fontWeight: sel ? 600 : 400,
                          transition: "all 0.14s",
                          border: `1.5px solid ${sel ? C.primary : C.border}`,
                          background: sel ? C.primaryLight : "transparent",
                          color: sel ? C.primary : C.textMid,
                        }}
                      >
                        {f.display}
                      </button>
                    );
                  })}
                </div>
              </F>
            </div>
          </Card>

          {/* ── Authorization ── */}
          <Card>
            <SH title="Authorization" subtitle="Does your insurance require pre-authorization?" icon="🔑" />
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              <F label="Authorization Required?">
                <RadioPill
                  value={form.primaryInsuranceAuthorizationRequiredYesNo}
                  onChange={handleAuth}
                  options={[
                    ["1", "Yes"],
                    ["0", "No"],
                  ]}
                />
              </F>
              {showAuth && (
                <>
                  <G cols={2}>
                    <F label="Authorization Number">
                      <TI value={form.primaryInsuranceAuthorizationNumber} onChange={(v) => setField("primaryInsuranceAuthorizationNumber", v)} placeholder="Auth #" />
                    </F>
                    <F label="Count Authorized" required error={e.countAuthorized}>
                      <TI value={form.countAuthorized} onChange={(v) => setField("countAuthorized", v.replace(/\D/g, "").slice(0, 4))} err={!!e.countAuthorized} placeholder="e.g. 12" />
                    </F>
                  </G>
                  <G cols={2}>
                    <F label="Effective Start">
                      <TI value={form.effectiveStart} onChange={(v) => setField("effectiveStart", v)} placeholder="MM/DD/YYYY" />
                    </F>
                    <F label="Effective End">
                      <TI value={form.effectiveEnd} onChange={(v) => setField("effectiveEnd", v)} placeholder="MM/DD/YYYY" />
                    </F>
                  </G>
                  <G cols={2}>
                    <F label="Auth Phone Type">
                      <Sel value={form.authPhoneType} onChange={(v) => setField("authPhoneType", v)} options={AUTH_PHONE_TYPES} />
                    </F>
                    <F label="Auth Phone">
                      <Phone
                        ac={form.authTelephoneAreaCode}
                        n1={form.authTelephoneLocalNumber1}
                        n2={form.authTelephoneLocalNumber2}
                        onAC={(v) => setField("authTelephoneAreaCode", v)}
                        onN1={(v) => setField("authTelephoneLocalNumber1", v)}
                        onN2={(v) => setField("authTelephoneLocalNumber2", v)}
                      />
                    </F>
                  </G>
                  <F label="Additional Auth Info">
                    <TI value={form.additionalInfoAuth} onChange={(v) => setField("additionalInfoAuth", v)} />
                  </F>
                </>
              )}
            </div>
          </Card>

          {/* ── Prescription ── */}
          <Card>
            <SH title="Prescription" subtitle="Do you have a physician prescription for treatment?" icon="💊" />
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              <F label="Do you have a prescription?">
                <RadioPill
                  value={form.prescriptionInformationYesNo}
                  onChange={handleRx}
                  options={[
                    ["1", "Yes"],
                    ["0", "No"],
                  ]}
                />
              </F>
              {showRx && (
                <>
                  <G cols={2}>
                    <F label="Start Date" required error={e.prescriptionStartDate}>
                      <TI value={form.prescriptionStartDate} onChange={(v) => setField("prescriptionStartDate", v)} placeholder="MM/DD/YYYY" err={!!e.prescriptionStartDate} />
                    </F>
                    <F label="End Date" required error={e.prescriptionEndDate}>
                      <TI value={form.prescriptionEndDate} onChange={(v) => setField("prescriptionEndDate", v)} placeholder="MM/DD/YYYY" err={!!e.prescriptionEndDate} />
                    </F>
                  </G>
                  <G cols={3}>
                    <F label="# of Visits">
                      <TI value={form.prescriptionNumberOfVisits} onChange={(v) => setField("prescriptionNumberOfVisits", v.replace(/\D/g, ""))} />
                    </F>
                    <F label="Alert Days Prior">
                      <TI value={form.prescriptionAlertDaysPrior} onChange={(v) => setField("prescriptionAlertDaysPrior", v.replace(/\D/g, ""))} />
                    </F>
                    <F label="Alert Visits Prior">
                      <TI value={form.prescriptionAlertVisitsPrior} onChange={(v) => setField("prescriptionAlertVisitsPrior", v.replace(/\D/g, ""))} />
                    </F>
                  </G>
                  <G cols={2}>
                    <F label="Frequency">
                      <Sel
                        value={form.prescriptionFrequency}
                        onChange={(v) => {
                          setField("prescriptionFrequency", v);
                          if (v !== "Custom") setField("prescriptionCustomFrequency", "");
                        }}
                        options={PRESCRIPTION_FREQUENCIES}
                        emptyOption={false}
                      />
                    </F>
                    {form.prescriptionFrequency === "Custom" && (
                      <F label="Custom Frequency">
                        <TI value={form.prescriptionCustomFrequency} onChange={(v) => setField("prescriptionCustomFrequency", v)} placeholder="Describe…" />
                      </F>
                    )}
                    <F label="Duration">
                      <Sel
                        value={form.prescriptionDuration}
                        onChange={(v) => {
                          setField("prescriptionDuration", v);
                          if (v !== "Custom") setField("prescriptionCustomDuration", "");
                        }}
                        options={PRESCRIPTION_DURATIONS}
                        emptyOption={false}
                      />
                    </F>
                    {form.prescriptionDuration === "Custom" && (
                      <F label="Custom Duration">
                        <TI value={form.prescriptionCustomDuration} onChange={(v) => setField("prescriptionCustomDuration", v)} placeholder="Describe…" />
                      </F>
                    )}
                  </G>
                </>
              )}
            </div>
          </Card>

          {/* ── Document ── */}
          <Card>
            <SH title="Document Upload" subtitle="Attach any relevant documents to your intake." icon="📎" />
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              <G cols={2}>
                <F label="Document Name">
                  <TI value={form.UserDefName} onChange={(v) => setField("UserDefName", v)} />
                </F>
                <F label="Document Type">
                  <Sel
                    value={form.documentTypeID}
                    onChange={(v) => {
                      setField("documentTypeID", v);
                      setField("documentType", DOCUMENT_TYPES.find((d) => d[0] === v)?.[1] || "");
                    }}
                    options={DOCUMENT_TYPES}
                    emptyOption={false}
                  />
                </F>
              </G>
              <G cols={3}>
                <F label="Date of Receipt">
                  <TI value={form.receiptDate} onChange={(v) => setField("receiptDate", v)} placeholder="MM/DD/YYYY" />
                </F>
                <F label="Expiration Date">
                  <TI value={form.expirationDate} onChange={(v) => setField("expirationDate", v)} placeholder="MM/DD/YYYY" />
                </F>
                <F label="Date Signed">
                  <TI value={form.dateSigned} onChange={(v) => setField("dateSigned", v)} placeholder="MM/DD/YYYY" />
                </F>
              </G>
              <F label="Signing Physician">
                <Sel
                  value={String(form.SignerPhysicianID)}
                  onChange={(v) => {
                    setField("SignerPhysicianID", v);
                    setField("SignerPhysician", MOCK_PHYSICIANS.find((p) => String(p.PhysicianID) === v)?.Name || "");
                  }}
                  options={MOCK_PHYSICIANS.map((p) => [String(p.PhysicianID), p.Name] as [string, string])}
                />
              </F>
            </div>
          </Card>

          {/* ── DPI ── */}
          <Card>
            <SH title="Digital Patient Intake" icon="📱" />
            <F label="Send Digital Patient Intake Now?">
              <RadioPill
                value={form.addDpiSelectorOnNewCaseYesNo}
                onChange={(v) => setField("addDpiSelectorOnNewCaseYesNo", v)}
                options={[
                  ["1", "Yes"],
                  ["0", "No"],
                ]}
              />
            </F>
          </Card>

          {/* ── Privacy Notice + Submit ── */}
          <div
            style={{
              background: C.primaryLight,
              border: `1px solid rgba(94,120,238,0.25)`,
              borderRadius: 12,
              padding: "16px 20px",
              marginBottom: 20,
              display: "flex",
              gap: 12,
              alignItems: "flex-start",
            }}
          >
            <span style={{ fontSize: 18, flexShrink: 0 }}>🔒</span>
            <p style={{ margin: 0, fontSize: 13, color: C.textMid, lineHeight: 1.65, fontFamily: "'DM Sans',sans-serif" }}>
              Your information is protected and will only be used to coordinate your care. By submitting, you confirm that all information provided is accurate to the best of your knowledge.
            </p>
          </div>

          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            {onCancel ? (
              <button onClick={onCancel} style={btnSecondary}>
                Cancel
              </button>
            ) : (
              <div />
            )}
            <button onClick={handleSubmit} style={{ ...btnPrimary, background: `linear-gradient(135deg, ${C.primary} 0%, ${C.primaryDark} 100%)` }}>
              Submit Registration →
            </button>
          </div>

          <p style={{ textAlign: "center" as const, marginTop: 28, fontSize: 12, color: C.textSoft, lineHeight: 1.6 }}>
            Need help? Call <strong style={{ color: C.textMid }}>(800) 555-0100</strong> or email <strong style={{ color: C.textMid }}>support@practice.com</strong>
          </p>
        </div>
      </div>
    </>
  );
}
